// jsaForm/actions.js

// Base actions
import { makeJsaBaseActions } from "./base/jsaFormBaseActions";
// Core actions
import { makeJsaCoreBasicInfoActions } from "./core/jsaFormCoreBasicInfoActions";
import { makeJsaCoreRacInfoActions } from "./core/jsaFormCoreRacInfoActions";
import { makeJsaCoreTrainingInfoActions } from "./core/jsaFormCoreTrainingActions";
import { makeJsaCoreSignatureInfoActions } from "./core/jsaFormCoreSignatureActions";
// V1 actions
import { makeJsaV1CsActions } from "./v1/jsaFormV1CseActions";
import { makeJsaV1EmergencyEquipmentActions } from "./v1/jsaFormV1EmergencyEquipmentActions";
import { makeJsaV1FallProtectionActions } from "./v1/jsaFormV1FallProtectionActions";
import { makeJsaV1HazComActions } from "./v1/jsaFormV1HazComActions";
import { makeJsaV1PermitsActions } from "./v1/jsaFormV1PermitsActions";
import { makeJsaV1PpeActions } from "./v1/jsaFormV1PpeActions";
// V2 actions
import { makeJsaV2HazardsActions } from "./v2/jsaFormV1HazardsActions";
import { makeJsaV2ControlMeasuresActions } from "./v2/jsaFormV1ControlMeasuresActions";
// Autosave errors actions
import { makeJsaAutosaveErrorsActions } from "./autosaveErrors/jsaFormAutosaveErrorsActions";

// Broadcast handler model imports
import { JsaTrainingEmployeesInfo } from "../../../models/Jsa/core/JsaTrainingInfo";
import { JsaEmployeeSignaturesInfo } from "../../../models/Jsa/core/JsaEmployeeSignaturesInfo";
import { JsaNonEmployeeSignaturesInfo } from "../../../models/Jsa/core/JsaNonEmployeeSignaturesInfo";
import { JsaFallProtectionInspectionList } from "../../../models/Jsa/v1/JsaFallProtection";

// Provide helpers specialized for the tab slice that owns the DEL + JSA.
// formKey identifies which form slice contains jsaFormUI (e.g. "delForm",
// "safetyAuditForm"). Defaults to "delForm" for backwards compatibility.
const makeHelpers = (set, get, tabKey, formKey = "delForm") => ({
  bump: (patch) => {
    const s = get();
    const prev = s[tabKey] || {};
    const nextRev = (prev.rev || 0) + 1;
    set({ [tabKey]: { ...prev, rev: nextRev, ...(patch || {}) } });
  },
  setJsaUi: (patch) => {
    const s = get();
    const curr = s[tabKey];
    const prevUI = curr?.[formKey]?.jsaFormUI || {};
    const nextUI =
      typeof patch === "function"
        ? patch(prevUI)
        : Object.keys(patch).reduce(
            (acc, key) => {
              const val = patch[key];
              if (typeof val === "function") {
                acc[key] = val(prevUI[key]);
              } else if (key === "core") {
                acc[key] =
                  typeof val === "function"
                    ? val(prevUI[key])
                    : { ...(prevUI[key] || {}), ...val };
              } else {
                acc[key] = { ...(prevUI[key] || {}), ...val };
              }
              return acc;
            },
            { ...prevUI },
          );
    set({
      [tabKey]: { ...curr, [formKey]: { ...curr[formKey], jsaFormUI: nextUI } },
    });
  },
  // Helper to conditionally wrap JSA patches for parent form service routing
  // Accesses isEmbeddedInForm from JSA state to determine if wrapping is needed
  // Injects del_id (from DEL or Sampling Event) for database linking
  emitJsaPatch: (patch, emitPatch) => {
    if (!emitPatch || !patch) return;

    // Get state to check embedding and get parent ID
    const s = get();
    const isEmbeddedInForm =
      s[tabKey]?.[formKey]?.jsaFormUI?.base?.isEmbeddedInForm || false;

    // Get the parent form ID - could be DEL or Sampling Event
    // JSA tables use del_id column for both (stores sampling_event_id for Sampling Event JSAs)
    const delId = s[tabKey]?.currentDel?.id;
    const samplingEventId = s[tabKey]?.currentSamplingEvent?.id;
    const parentId = delId || samplingEventId;

    // Inject del_id into the patch for database linking
    const patchWithParentId = {
      ...patch,
      del_id: parentId,
    };

    if (isEmbeddedInForm) {
      // Wrap JSA patch for routing through parent form's service (DEL, Sampling Event, etc.)
      // Parent service expects: { patch_type: "jsa", jsa_data: {...} }
      const wrappedPatch = {
        patch_type: "jsa",
        jsa_data: patchWithParentId,
      };
      emitPatch(wrappedPatch);
    } else {
      // Standalone JSA - send patch directly to JSA socket service
      emitPatch(patchWithParentId);
    }
  },
});

// --- Shared JSA Broadcast Utilities ---

const toCamelCase = (str) =>
  str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

/**
 * Apply a snake_case patch to a camelCase model instance.
 * Tries set<PascalCase>() method first, falls back to direct property assignment.
 */
const applyPatchToObject = (target, patch, skipKeys = ["id", "action"]) => {
  Object.entries(patch).forEach(([key, value]) => {
    if (skipKeys.includes(key)) return;
    const camelKey = toCamelCase(key);
    const setterName = `set${camelKey.charAt(0).toUpperCase()}${camelKey.slice(1)}`;
    if (typeof target[setterName] === "function") {
      target[setterName](value);
    } else if (camelKey in target) {
      target[camelKey] = value;
    }
  });
};

// --- Array Operation Helpers ---

/**
 * Handle training employees array updates (add/update/remove).
 * Training employees array is at: jsa.jsaTrainingInfo.jsaTrainingEmployeesInfo
 */
const handleTrainingEmployeesBroadcast = (jsa, patchData, action) => {
  const employees = jsa.jsaTrainingInfo?.jsaTrainingEmployeesInfo || [];
  const { id } = patchData;

  if (action === "remove") {
    const idx = employees.findIndex((e) => e.rowId === id || e.id === id);
    if (idx >= 0) employees.splice(idx, 1);
  } else {
    const existing = employees.find((e) => e.rowId === id || e.id === id);
    if (existing) {
      if (patchData.employee_id !== undefined)
        existing.employeeId = patchData.employee_id;
      if (patchData.employee_name !== undefined)
        existing.employeeName = patchData.employee_name;
    } else {
      const newEmployee = new JsaTrainingEmployeesInfo({
        id: patchData.id,
        employee_id: patchData.employee_id,
        employee_name: patchData.employee_name,
      });
      employees.push(newEmployee);
    }
  }
};

/**
 * Handle employee signatures array updates (add/update/remove).
 * Signatures array is at: jsa.jsaEmployeeSignaturesInfo
 */
const handleSignaturesBroadcast = (jsa, patchData, action) => {
  const signatures = jsa.jsaEmployeeSignaturesInfo || [];
  const { id } = patchData;

  if (action === "remove") {
    const idx = signatures.findIndex((s) => s.rowId === id || s.id === id);
    if (idx >= 0) signatures.splice(idx, 1);
  } else {
    const existing = signatures.find((s) => s.rowId === id || s.id === id);
    if (existing) {
      if (patchData.employee_id !== undefined)
        existing.employeeId = patchData.employee_id;
      if (patchData.employee_name !== undefined)
        existing.employeeName = patchData.employee_name;
      if (patchData.employee_sig !== undefined)
        existing.employeeSig = patchData.employee_sig
          ? "data:image/png;base64," + patchData.employee_sig
          : "";
    } else {
      const newSignature = new JsaEmployeeSignaturesInfo({
        id: patchData.id,
        employee_id: patchData.employee_id,
        employee_name: patchData.employee_name,
        employee_sig: patchData.employee_sig,
      });
      signatures.push(newSignature);
    }
  }
};

/**
 * Handle non-employee signatures array updates (add/update/remove).
 * Signatures array is at: jsa.jsaNonEmployeeSignaturesInfo
 */
const handleNonEmpSignaturesBroadcast = (jsa, patchData, action) => {
  const signatures = jsa.jsaNonEmployeeSignaturesInfo || [];
  const { id } = patchData;

  if (action === "remove") {
    const idx = signatures.findIndex((s) => s.rowId === id || s.id === id);
    if (idx >= 0) signatures.splice(idx, 1);
  } else {
    const existing = signatures.find((s) => s.rowId === id || s.id === id);
    if (existing) {
      if (patchData.non_employee_name !== undefined)
        existing.nonEmployeeName = patchData.non_employee_name;
      if (patchData.company_name !== undefined)
        existing.nonEmployeeCompany = patchData.company_name;
      if (patchData.non_employee_sig !== undefined)
        existing.nonEmployeeSig = patchData.non_employee_sig
          ? "data:image/png;base64," + patchData.non_employee_sig
          : "";
    } else {
      const newSignature = new JsaNonEmployeeSignaturesInfo({
        id: patchData.id,
        non_employee_name: patchData.non_employee_name,
        company_name: patchData.company_name,
        non_employee_sig: patchData.non_employee_sig,
      });
      signatures.push(newSignature);
    }
  }
};

/**
 * Handle fall protection inspection list array updates (add/update/remove).
 * V1 only. Inspection list array is at: jsa.jsaFallProtection.fallProtectionInspectionList
 *
 * NOTE: For updates, we use applyPatchToObject which does direct property assignment.
 * The JsaFallProtectionInspectionList class has update*() methods with mutual-exclusion
 * logic (e.g., setting accept=true also sets reject=false), but for broadcasts we want
 * direct assignment because the server has already applied that logic and sends
 * authoritative values.
 */
const handleFallProtectionInspectionListBroadcast = (
  fallProtection,
  patchData,
  action
) => {
  const inspectionList = fallProtection.fallProtectionInspectionList || [];
  const { id } = patchData;

  if (action === "remove") {
    const idx = inspectionList.findIndex(
      (item) => item.rowId === id || item.id === id
    );
    if (idx >= 0) inspectionList.splice(idx, 1);
  } else {
    const existing = inspectionList.find(
      (item) => item.rowId === id || item.id === id
    );
    if (existing) {
      applyPatchToObject(existing, patchData, [
        "patch_type",
        "jsa_id",
        "id",
        "action",
      ]);
    } else {
      const newItem = new JsaFallProtectionInspectionList(patchData);
      inspectionList.push(newItem);
    }
  }
};

// --- Main Shared Broadcast Handler ---

/**
 * Apply a JSA broadcast patch to a JSA model instance (JsaV1 or JsaV2).
 * Mutates the JSA in place. Handles all JSA section patch_types.
 *
 * @param {JsaV1|JsaV2} jsa - The JSA model instance to mutate
 * @param {object} patchData - Broadcast payload: { patch_type, jsa_id, action?, ...fields }
 *
 * Caller is responsible for:
 *   1. Locating the correct JSA object
 *      - DEL: get()[tabKey].currentDel.jsa
 *      - Sampling Event: find by jsa_id from event.jsa[] array
 *   2. Calling bump() after this function returns to trigger re-render
 */
export const applyJsaBroadcastPatch = (jsa, patchData) => {
  if (!jsa || !patchData) return;

  const { patch_type, action } = patchData;

  switch (patch_type) {
    // === CORE (shared V1/V2) ===

    case "jsa_basic_info": {
      // Transform supervisor_id/supervisor_name to the model's {value, label} format
      if (patchData.supervisor_id !== undefined) {
        jsa.jsaBasicInfo.supervisor = {
          value: patchData.supervisor_id || "",
          label: patchData.supervisor_name || "",
        };
      }
      // Transform site_location string to the model's object format
      if (patchData.site_location !== undefined) {
        jsa.jsaBasicInfo.siteLocation = patchData.site_location
          ? { label: patchData.site_location, value: "", lat: "", lng: "" }
          : null;
      }
      // Handle jsa_completed_date - assign directly
      if (patchData.jsa_completed_date !== undefined) {
        jsa.jsaBasicInfo.jsaCompletedDate = patchData.jsa_completed_date;
      }
      // Handle jsa_completed_time - assign directly
      if (patchData.jsa_completed_time !== undefined) {
        jsa.jsaBasicInfo.jsaCompletedTime = patchData.jsa_completed_time;
      }
      // Apply remaining fields via generic helper
      applyPatchToObject(jsa.jsaBasicInfo, patchData, [
        "patch_type",
        "jsa_id",
        "supervisor_id",
        "supervisor_name",
        "site_location",
        "jsa_completed_date",
        "jsa_completed_time",
      ]);
      break;
    }

    case "jsa_rac_info":
      applyPatchToObject(jsa.jsaRacInfo, patchData, ["patch_type", "jsa_id"]);
      break;

    case "jsa_training":
      applyPatchToObject(jsa.jsaTrainingInfo, patchData, [
        "patch_type",
        "jsa_id",
      ]);
      break;

    case "jsa_training_employees":
      handleTrainingEmployeesBroadcast(jsa, patchData, action);
      break;

    case "jsa_signatures":
      handleSignaturesBroadcast(jsa, patchData, action);
      break;

    case "jsa_non_emp_signatures":
      handleNonEmpSignaturesBroadcast(jsa, patchData, action);
      break;

    // === V1 ONLY ===
    // Guarded with existence checks - these properties don't exist on JsaV2

    case "jsa_confined_space":
      if (jsa.jsaConfinedSpace) {
        applyPatchToObject(jsa.jsaConfinedSpace, patchData, [
          "patch_type",
          "jsa_id",
        ]);
      }
      break;

    case "jsa_emergency_equipment":
      if (jsa.jsaEmergencyEquipment) {
        applyPatchToObject(jsa.jsaEmergencyEquipment, patchData, [
          "patch_type",
          "jsa_id",
        ]);
      }
      break;

    case "jsa_fall_protection":
      if (jsa.jsaFallProtection) {
        applyPatchToObject(jsa.jsaFallProtection, patchData, [
          "patch_type",
          "jsa_id",
        ]);
      }
      break;

    case "jsa_fall_protection_inspection_list":
      if (jsa.jsaFallProtection) {
        handleFallProtectionInspectionListBroadcast(
          jsa.jsaFallProtection,
          patchData,
          action
        );
      }
      break;

    case "jsa_haz_com":
      if (jsa.jsaHazCom) {
        applyPatchToObject(jsa.jsaHazCom, patchData, [
          "patch_type",
          "jsa_id",
        ]);
      }
      break;

    case "jsa_permit_info":
      if (jsa.jsaPermitInfo) {
        applyPatchToObject(jsa.jsaPermitInfo, patchData, [
          "patch_type",
          "jsa_id",
        ]);
      }
      break;

    case "jsa_ppe_info":
      if (jsa.jsaPpeInfo) {
        applyPatchToObject(jsa.jsaPpeInfo, patchData, [
          "patch_type",
          "jsa_id",
        ]);
      }
      break;

    // === V2 ONLY ===
    // Guarded with existence checks - these properties don't exist on JsaV1

    case "jsa_hazards_v2":
      if (jsa.jsaHazardsV2) {
        applyPatchToObject(jsa.jsaHazardsV2, patchData, [
          "patch_type",
          "jsa_id",
        ]);
      }
      break;

    case "jsa_control_measures_v2":
      if (jsa.jsaControlMeasuresV2) {
        applyPatchToObject(jsa.jsaControlMeasuresV2, patchData, [
          "patch_type",
          "jsa_id",
        ]);
      }
      break;

    default:
      console.warn("applyJsaBroadcastPatch: Unknown patch_type:", patch_type);
      return;
  }
};

export const makeJsaActions = (set, get, tabKey, formKey = "delForm") => {
  const h = makeHelpers(set, get, tabKey, formKey);

  /**
   * Handle incoming JSA broadcasts for DEL context.
   * DEL has a single JSA at currentDel.jsa.
   * Caller (useDelAutosave hook) calls bump() after this returns.
   * @param {object} patchData - { patch_type, jsa_id, action?, ...fields }
   */
  const handleJsaBroadcast = (patchData) => {
    const jsa = get()[tabKey]?.currentDel?.jsa;
    if (!jsa) {
      console.warn("handleJsaBroadcast: No JSA found on currentDel");
      return;
    }
    applyJsaBroadcastPatch(jsa, patchData);
  };

  return Object.freeze({
    base: makeJsaBaseActions(h),
    core: {
      ...makeJsaCoreBasicInfoActions(h),
      ...makeJsaCoreRacInfoActions(h),
      ...makeJsaCoreTrainingInfoActions(h),
      ...makeJsaCoreSignatureInfoActions(h),
    },
    v1: {
      ...makeJsaV1CsActions(h),
      ...makeJsaV1EmergencyEquipmentActions(h),
      ...makeJsaV1FallProtectionActions(h),
      ...makeJsaV1HazComActions(h),
      ...makeJsaV1PermitsActions(h),
      ...makeJsaV1PpeActions(h),
    },
    v2: {
      ...makeJsaV2HazardsActions(h),
      ...makeJsaV2ControlMeasuresActions(h),
    },
    autosaveErrors: makeJsaAutosaveErrorsActions(h),
    handleJsaBroadcast,
  });
};
